import { Box, Button, Chip, Stack, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { compact, isEmpty, values } from 'lodash';

import { updateCentralFillQueueFilter } from 'store/thunks/workQueueThunks';
import { casingFormat } from 'common/utils';

const ERROR_STATUSES = new Set([
  'SUBMIT_FAILED',
  'CLINIC_NOT_CONFIGURED',
  'CLINIC_AUTOCREATE_FAILED',
  'PROVIDER_RESOLUTION_FAILED',
  'PATIENT_RESOLUTION_FAILED',
  'PRODUCT_NOT_MAPPED',
  'EXCEPTION',
  'MISSING_REASON_FOR_COMPOUNDING',
]);

const STATUS_LABEL = {
  QUEUED: 'Queued',
  SUBMITTED: 'Submitted',
  ACCEPTED: 'Accepted',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  VOIDED: 'Voided',
  ROUTE_TO_DEFAULT: 'Route to Default',
};

const DRUG_NAME_CHIP_CHAR_LIMIT = 50;

const formatDateForChip = (isoDate) => {
  if (!isoDate) return '…';
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${month}/${day}/${year}`;
};

const formatStatusLabel = (statuses) => {
  const hasError = statuses.some((s) => ERROR_STATUSES.has(s));
  const nonErrorLabels = statuses
    .filter((s) => !ERROR_STATUSES.has(s))
    .map((s) => STATUS_LABEL[s] ?? s);
  return [...nonErrorLabels, ...(hasError ? ['Error'] : [])].join(', ');
};

const CentralFillQueueFilterStatusBar = () => {
  const dispatch = useDispatch();
  const filter = useSelector(({ workQueue }) => workQueue.compoundingQueue?.filter);
  const partners = useSelector(
    ({ auth, partner: partnerStore }) => partnerStore[auth.currentTenantId]
  );

  const partnerOptions = partners?.map(({ partnerName, partnerUuid }) => ({
    value: partnerUuid,
    label: casingFormat(partnerName),
  }));

  const removeFilter = (filterName) => () => {
    dispatch(
      updateCentralFillQueueFilter({
        filter: {
          ...filter,
          [filterName]: undefined,
        },
      })
    );
  };

  const removeAllFilter = () => dispatch(updateCentralFillQueueFilter({ filter: {} }));

  const hasActiveFilters = !isEmpty(compact(values(filter)));

  return (
    <Stack spacing={1} direction='row' flexWrap='wrap' rowGap={1} alignItems='center'>
      {filter?.orderId && (
        <Chip label={`Order ID = ${filter.orderId}`} onDelete={removeFilter('orderId')} />
      )}

      {filter?.statuses && !isEmpty(filter.statuses) && (
        <Chip
          label={`Status = ${formatStatusLabel(filter.statuses)}`}
          onDelete={removeFilter('statuses')}
        />
      )}

      {filter?.partnerIds && !isEmpty(filter.partnerIds) && (
        <Chip
          label={`Partner = ${
            partnerOptions
              ?.filter((opt) => filter.partnerIds.includes(opt.value))
              .map((opt) => opt.label)
              .join(', ') || `${filter.partnerIds.length} selected`
          }`}
          onDelete={removeFilter('partnerIds')}
        />
      )}

      {filter?.compoundingPharmacyIds && !isEmpty(filter.compoundingPharmacyIds) && (
        <Chip
          label={`Pharmacy (${filter.compoundingPharmacyIds.length} selected)`}
          onDelete={removeFilter('compoundingPharmacyIds')}
        />
      )}

      {filter?.drugNameContains && (
        <Chip
          label={
            `Drug = ${filter.drugNameContains}`.length > DRUG_NAME_CHIP_CHAR_LIMIT ? (
              <Tooltip title={`Drug = ${filter.drugNameContains}`}>
                <Box
                  sx={{
                    maxWidth: '20rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`Drug = ${filter.drugNameContains}`}
                </Box>
              </Tooltip>
            ) : (
              `Drug = ${filter.drugNameContains}`
            )
          }
          onDelete={removeFilter('drugNameContains')}
        />
      )}

      {(filter?.submittedAfter || filter?.submittedBefore) && (
        <Chip
          label={`Date = ${formatDateForChip(filter.submittedAfter)} – ${formatDateForChip(filter.submittedBefore)}`}
          onDelete={() =>
            dispatch(
              updateCentralFillQueueFilter({
                filter: {
                  ...filter,
                  submittedAfter: undefined,
                  submittedBefore: undefined,
                },
              })
            )
          }
        />
      )}

      {hasActiveFilters && <Button onClick={removeAllFilter}>Remove All</Button>}
    </Stack>
  );
};

export default CentralFillQueueFilterStatusBar;
